import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useDebounce } from './useDebounce';
import { ALL_UNITS_FILTER, HANDOFF_TYPE_FILTER_ALL, type FilterConfig, type SortConfig } from './usePatients';
import { clearProgressNotes, clearProgressNotesError } from '@/store/slices/patients';
import { downloadProgressNotePatients, fetchProgressNotesPatients, fetchUnits } from '@/store/slices/patients/patients-api';
import type { UnitOption } from '@/types/patient';
import { applyCensusHospitalName, mapProgressNoteDownloadToSheetPayloads } from '@/utils/census/map-census-detail-to-payload';
import { registerSessionCacheInvalidator } from '@/services/auth-session';

interface UseProgressNotesOptions {
    autoFetch?: boolean;
    unitRangeId?: string;
    isSelected?: boolean | null;
}

let lastLoadedProgressNotesQueryKey: string | null = null;
let lastLoadedProgressNotesUnitsHospitalId: string | null = null;
let pdfExportModulePromise: Promise<typeof import('@/utils/pdf/export-census-pdf')> | null = null;

/** Clears in-memory reuse keys so the next listing mount refetches. */
export const invalidateProgressNotesCache = () => {
    lastLoadedProgressNotesQueryKey = null;
    lastLoadedProgressNotesUnitsHospitalId = null;
};

registerSessionCacheInvalidator(invalidateProgressNotesCache);

const loadPdfExportModule = () => {
    if (!pdfExportModulePromise) {
        pdfExportModulePromise = import('@/utils/pdf/export-census-pdf');
    }
    return pdfExportModulePromise;
};

export const tierOptions: { value: string; label: string }[] = [
    { value: 'all', label: 'All Tiers' },
    { value: 'T1', label: 'T1' },
    { value: 'T2', label: 'T2' },
    { value: 'T3', label: 'T3' }
];

const normalizeHandOffTypeFilter = (value: string | null): '' | 'weekend' | 'general' => {
    if (value === 'weekend' || value === 'general') {
        return value;
    }
    return HANDOFF_TYPE_FILTER_ALL;
};

export const useProgressNotes = ({ autoFetch = true, unitRangeId, isSelected = true }: UseProgressNotesOptions = {}) => {
    const dispatch = useAppDispatch();
    const { hospitalId: hospitalIdFromRoute } = useParams<{ hospitalId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsString = searchParams.toString();

    const {
        progressNotesPatients,
        units,
        progressNotesTotal,
        progressNotesServerPage,
        progressNotesServerLimit,
        progressNotesLoading,
        unitsLoading,
        progressNotesError,
        unitsError
    } = useAppSelector((state) => state.patients);
    const { currentHospitalId, hospitals: userHospitals = [] } = useAppSelector(({ hospital: hospitalState }) => hospitalState);
    const adminHospitals = useAppSelector(({ hospitalAdmin }) => hospitalAdmin?.hospitals ?? []);
    const hospitalKey = hospitalIdFromRoute ?? currentHospitalId ?? null;
    const hospitalName = useMemo(() => {
        if (!hospitalKey) {
            return '';
        }
        return (
            adminHospitals.find((hospital) => hospital.id === hospitalKey)?.name?.trim() ||
            userHospitals.find((hospital) => hospital.id === hospitalKey)?.name?.trim() ||
            ''
        );
    }, [adminHospitals, hospitalKey, userHospitals]);
    const initialQueryState = useMemo(() => {
        const params = new URLSearchParams(searchParamsString);
        const initialPage = Number(params.get('page') ?? '1');
        const initialLimit = Number(params.get('limit') ?? '50');
        const initialSortOrder = params.get('sortOrder');

        return {
            search: params.get('search') ?? '',
            unit: params.get('unit'),
            tier: params.get('tier'),
            handOffType: normalizeHandOffTypeFilter(params.get('handOffType')),
            page: Number.isFinite(initialPage) && initialPage > 0 ? initialPage - 1 : 0,
            limit: Number.isFinite(initialLimit) && initialLimit > 0 ? initialLimit : 50,
            sortBy: params.get('sortBy'),
            sortDirection: initialSortOrder === 'DESC' ? ('desc' as const) : ('asc' as const)
        };
    }, [searchParamsString]);

    const [inputValue, setInputValue] = useState(initialQueryState.search);
    const debouncedSearch = useDebounce(inputValue, 300);

    const [sortConfig, setSortConfig] = useState<SortConfig>({
        key: initialQueryState.sortBy ?? null,
        direction: initialQueryState.sortDirection
    });

    const [filterConfig, setFilterConfig] = useState<FilterConfig>({
        gender: null,
        unit: initialQueryState.unit ?? null,
        tier: initialQueryState.tier ?? null,
        readmitRisk: null,
        dcToday: null,
        handOffType: initialQueryState.handOffType
    });

    const [page, setPage] = useState(initialQueryState.page);
    const [rowsPerPage, setRowsPerPage] = useState(initialQueryState.limit);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const activeRequestRef = useRef<{ abort?: () => void } | null>(null);
    const hasUserChangedRowsPerPageRef = useRef(false);
    const previousHospitalIdRef = useRef(hospitalKey);
    const isFirstSearchSyncRef = useRef(true);

    const loadUnits = useCallback(() => {
        dispatch(fetchUnits({ hospitalId: hospitalKey ?? undefined }));
    }, [dispatch, hospitalKey]);

    const selectedUnitId = useMemo(() => {
        if (!filterConfig.unit) return undefined;
        return units.find((unit) => unit.unitName === filterConfig.unit || unit.unitCode === filterConfig.unit)?.id;
    }, [filterConfig.unit, units]);

    const requestParams = useMemo(
        () => ({
            search: debouncedSearch.trim() || undefined,
            unitId: selectedUnitId,
            unitRangeId: unitRangeId || undefined,
            tier: filterConfig.tier || undefined,
            page: page + 1,
            limit: rowsPerPage,
            sortBy: sortConfig.key || undefined,
            sortOrder: sortConfig.key ? (sortConfig.direction === 'asc' ? ('ASC' as const) : ('DESC' as const)) : undefined,
            isSelected,
            handOffType: filterConfig.handOffType || undefined
        }),
        [
            debouncedSearch,
            selectedUnitId,
            unitRangeId,
            filterConfig.tier,
            filterConfig.handOffType,
            page,
            rowsPerPage,
            sortConfig.key,
            sortConfig.direction,
            isSelected
        ]
    );

    const queryKey = useMemo(
        () =>
            JSON.stringify({
                hospitalId: hospitalKey,
                endpoint: 'progress-notes',
                ...requestParams
            }),
        [hospitalKey, requestParams]
    );

    const loadProgressNotes = useCallback(() => {
        activeRequestRef.current?.abort?.();

        const requestPayload = { ...requestParams, hospitalId: hospitalKey ?? undefined };
        const request = dispatch(fetchProgressNotesPatients(requestPayload));
        lastLoadedProgressNotesQueryKey = queryKey;
        activeRequestRef.current = request;
        return request;
    }, [dispatch, requestParams, queryKey, hospitalKey]);

    const clearAllProgressNotes = useCallback(() => {
        dispatch(clearProgressNotes());
    }, [dispatch]);

    const clearError = useCallback(() => {
        dispatch(clearProgressNotesError());
    }, [dispatch]);

    const handleDownloadCensusPdf = useCallback(async () => {
        setDownloadingPdf(true);
        try {
            const data = await dispatch(
                downloadProgressNotePatients({
                    hospitalId: hospitalKey ?? undefined,
                    unit: filterConfig.unit || ALL_UNITS_FILTER
                })
            ).unwrap();

            const payloads = applyCensusHospitalName(mapProgressNoteDownloadToSheetPayloads(data), hospitalName);
            if (!payloads.length) {
                toast.info('No census sheet data available for the selected unit.');
                return;
            }

            const { openCensusPdfInFullWindow, openCombinedCensusPdfInFullWindow } = await loadPdfExportModule();
            const result =
                payloads.length === 1 ? await openCensusPdfInFullWindow(payloads[0]) : await openCombinedCensusPdfInFullWindow(payloads);

            if (!result.ok) {
                toast.error(
                    result.reason === 'popup_blocked'
                        ? 'Pop-up blocked. Allow pop-ups for this site to view the PDF.'
                        : 'Unable to generate PDF. Please try again.'
                );
            }
        } catch {
            toast.error('Unable to open census PDF. Please try again.');
        } finally {
            setDownloadingPdf(false);
        }
    }, [dispatch, filterConfig.unit, hospitalKey, hospitalName]);

    const handleSort = useCallback((key: string) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    const handleFilter = useCallback((key: keyof FilterConfig, value: string | null) => {
        let didChange = false;
        const nextValue = key === 'handOffType' ? normalizeHandOffTypeFilter(value) : value;
        setFilterConfig((prev) => {
            if (prev[key] === nextValue) {
                return prev;
            }
            didChange = true;
            return {
                ...prev,
                [key]: nextValue
            };
        });
        if (didChange) {
            setPage((prev) => (prev === 0 ? prev : 0));
        }
    }, []);

    const handleChangePage = useCallback((newPage: number) => {
        setPage((prev) => (prev === newPage ? prev : newPage));
    }, []);

    const handleChangeRowsPerPage = useCallback((newRowsPerPage: number) => {
        hasUserChangedRowsPerPageRef.current = true;
        setRowsPerPage((prev) => (prev === newRowsPerPage ? prev : newRowsPerPage));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    const clearFilters = useCallback(() => {
        setFilterConfig((prev) => {
            if (!prev.gender && !prev.unit && !prev.tier && !prev.readmitRisk && !prev.dcToday && !prev.handOffType) {
                return prev;
            }
            return {
                gender: null,
                unit: null,
                tier: null,
                readmitRisk: null,
                dcToday: null,
                handOffType: HANDOFF_TYPE_FILTER_ALL
            };
        });
        setInputValue((prev) => (prev === '' ? prev : ''));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, []);

    const processedPatients = useMemo(() => {
        let result = [...progressNotesPatients];

        if (filterConfig.gender) {
            result = result.filter((p) => p.gender === filterConfig.gender);
        }
        if (filterConfig.readmitRisk) {
            result = result.filter((p) => p.readmitRisk === filterConfig.readmitRisk);
        }
        if (filterConfig.dcToday) {
            result = result.filter((p) => p.dcToday === filterConfig.dcToday);
        }

        return result;
    }, [progressNotesPatients, filterConfig]);

    const hasClientOnlyFilters = Boolean(filterConfig.gender || filterConfig.readmitRisk || filterConfig.dcToday);
    const effectiveTotalCount = hasClientOnlyFilters ? processedPatients.length : progressNotesTotal;

    const filterOptions = useMemo<{ units: string[] }>(() => {
        const mappedUnitOptions: string[] = units
            .map((unit: UnitOption) => unit.unitCode || unit.unitName)
            .filter((unit): unit is string => typeof unit === 'string' && unit.trim().length > 0);
        const unitsFromApi = [...new Set(mappedUnitOptions)].sort((a, b) => a.localeCompare(b));

        return {
            units: unitsFromApi
        };
    }, [units]);

    const unitOptions = useMemo<{ value: string; label: string }[]>(
        () => [{ value: 'all', label: 'All Units' }, ...filterOptions.units.map((unit) => ({ value: unit, label: unit }))],
        [filterOptions.units]
    );

    useEffect(() => {
        const shouldReuseUnits = units.length > 0 && lastLoadedProgressNotesUnitsHospitalId === hospitalKey;
        if (autoFetch && !shouldReuseUnits) {
            lastLoadedProgressNotesUnitsHospitalId = hospitalKey;
            loadUnits();
        }
    }, [autoFetch, loadUnits, units.length, hospitalKey]);

    useEffect(() => {
        if (!hasUserChangedRowsPerPageRef.current && progressNotesServerLimit && progressNotesServerLimit !== rowsPerPage) {
            setRowsPerPage(progressNotesServerLimit);
        }
    }, [progressNotesServerLimit, rowsPerPage]);

    useEffect(() => {
        const shouldReuse = progressNotesPatients.length > 0 && lastLoadedProgressNotesQueryKey === queryKey;
        if (autoFetch && !shouldReuse) {
            loadProgressNotes();
        }
    }, [autoFetch, loadProgressNotes, progressNotesPatients.length, queryKey]);

    useEffect(() => {
        if (previousHospitalIdRef.current === hospitalKey) {
            return;
        }

        previousHospitalIdRef.current = hospitalKey;
        lastLoadedProgressNotesQueryKey = null;
        lastLoadedProgressNotesUnitsHospitalId = null;
        setFilterConfig((prev) =>
            prev.unit || prev.tier || prev.handOffType ? { ...prev, unit: null, tier: null, handOffType: HANDOFF_TYPE_FILTER_ALL } : prev
        );
        setInputValue((prev) => (prev === '' ? prev : ''));
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [hospitalKey]);

    useEffect(() => {
        if (isFirstSearchSyncRef.current) {
            isFirstSearchSyncRef.current = false;
            return;
        }
        setPage((prev) => (prev === 0 ? prev : 0));
    }, [debouncedSearch]);

    useEffect(() => {
        const nextParams = new URLSearchParams(searchParamsString);
        if (debouncedSearch.trim()) {
            nextParams.set('search', debouncedSearch.trim());
        } else {
            nextParams.delete('search');
        }
        if (filterConfig.unit) {
            nextParams.set('unit', filterConfig.unit);
        } else {
            nextParams.delete('unit');
        }
        if (filterConfig.tier) {
            nextParams.set('tier', filterConfig.tier);
        } else {
            nextParams.delete('tier');
        }
        if (filterConfig.handOffType) {
            nextParams.set('handOffType', filterConfig.handOffType);
        } else {
            nextParams.delete('handOffType');
        }
        nextParams.set('page', String(page + 1));
        nextParams.set('limit', String(rowsPerPage));

        if (sortConfig.key) {
            nextParams.set('sortBy', String(sortConfig.key));
            nextParams.set('sortOrder', sortConfig.direction === 'asc' ? 'ASC' : 'DESC');
        } else {
            nextParams.delete('sortBy');
            nextParams.delete('sortOrder');
        }

        const nextParamsString = nextParams.toString();
        if (nextParamsString !== searchParamsString) {
            setSearchParams(nextParams, { replace: true });
        }
    }, [
        debouncedSearch,
        filterConfig.unit,
        filterConfig.tier,
        filterConfig.handOffType,
        page,
        rowsPerPage,
        searchParamsString,
        setSearchParams,
        sortConfig.direction,
        sortConfig.key
    ]);

    useEffect(
        () => () => {
            activeRequestRef.current?.abort?.();
        },
        []
    );

    return {
        patients: processedPatients,
        allPatients: progressNotesPatients,
        filteredCount: effectiveTotalCount,
        serverPage: progressNotesServerPage,
        serverLimit: progressNotesServerLimit,
        loading: progressNotesLoading,
        unitsLoading,
        error: progressNotesError,
        unitsError,
        inputValue,
        setInputValue,
        sortConfig,
        handleSort,
        filterConfig,
        filterOptions,
        unitOptions,
        handleFilter,
        clearFilters,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        loadProgressNotes,
        loadUnits,
        clearAllProgressNotes,
        clearError,
        downloadingPdf,
        handleDownloadCensusPdf
    };
};
